import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";
import { useAdminAuth } from "./AuthContext";

const NewCountContext = createContext(null);

// Counts untriaged contact messages (internal_status "new" or unset → "ukendt").
// Reuses the existing aggregated /api/admin/dashboard data — no new endpoint,
// no PII. null = unknown (badge hidden).
export const NewCountProvider = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const [newCount, setNewCount] = useState(null);
  const authed = !!(user && user.role && !user.must_change_password);

  const refresh = useCallback(async () => {
    if (!authed) return;
    try {
      const d = await api.dashboard();
      const s = d.by_internal_status || {};
      setNewCount((s.new || 0) + (s.ukendt || 0));
    } catch (err) {
      if (err && err.status === 401) {
        await logout(); // follow existing logout flow on session loss
      }
      // transient errors: keep last known value, no dramatic error
    }
  }, [authed, logout]);

  // Initial load + refresh when navigating within the admin area.
  useEffect(() => {
    if (authed) refresh();
    else setNewCount(null);
  }, [authed, location.pathname, refresh]);

  // Periodic polling (60s), paused when the tab is not active.
  useEffect(() => {
    if (!authed) return undefined;
    let id = null;
    const stop = () => {
      if (id) {
        clearInterval(id);
        id = null;
      }
    };
    const start = () => {
      stop();
      id = setInterval(() => {
        if (document.visibilityState === "visible") refresh();
      }, 60000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [authed, refresh]);

  return (
    <NewCountContext.Provider value={{ newCount, refresh }}>
      {children}
    </NewCountContext.Provider>
  );
};

export const useNewCount = () =>
  useContext(NewCountContext) || { newCount: null, refresh: () => {} };
